import { useI18n } from '../i18n/useI18n';
import type { SimpleKey } from '../i18n/types';
import { isTenantState } from '../api/types';

/**
 * A status is a word plus a glyph. Colour agrees with them and carries nothing
 * on its own — with the hues stripped, "ready" and "not accepted" still read as
 * different things. For a console where the difference between allowed and
 * refused is the whole product, that is not a nicety.
 *
 * Every state arrives as a string rather than as a union, and a value neither
 * side knows reads as unknown. The alternative is a console that crashes the
 * day the control plane learns a new state, which is the day it is least
 * convenient.
 */

export type Tone = 'ok' | 'warn' | 'deny' | 'info' | 'neutral';

/** Deliberately not letters: the lint rule keeps prose out of components. */
const GLYPH: Record<Tone, string> = {
  ok: '✓',
  warn: '!',
  deny: '✕',
  info: '•',
  neutral: '–',
};

/**
 * `hint` is what the word means, on the badge itself.
 *
 * A meaning that fits in a tooltip is a meaning that does not need a paragraph
 * beside every row of a table, and a table is where most of these stand.
 */
export function StatusBadge({ tone, label, hint }: { tone: Tone; label: string; hint?: string }) {
  return (
    <span className={`badge badge-${tone}`} {...(hint === undefined ? {} : { title: hint })}>
      <span className="bmark" aria-hidden="true">
        {GLYPH[tone]}
      </span>
      {label}
    </span>
  );
}

const TENANT_STATE_TONE: Record<string, Tone> = {
  requested: 'info',
  provisioning: 'info',
  ready: 'ok',
  failed: 'deny',
  suspended: 'neutral',
  tearing_down: 'warn',
  removed: 'neutral',
};

const TENANT_STATE_LABEL: Record<string, SimpleKey> = {
  requested: 'tenantState.requested',
  provisioning: 'tenantState.provisioning',
  ready: 'tenantState.ready',
  failed: 'tenantState.failed',
  suspended: 'tenantState.suspended',
  tearing_down: 'tenantState.tearingDown',
  removed: 'tenantState.removed',
};

const TENANT_STATE_HINT: Record<string, SimpleKey> = {
  requested: 'tenantState.requested.hint',
  provisioning: 'tenantState.provisioning.hint',
  ready: 'tenantState.ready.hint',
  failed: 'tenantState.failed.hint',
  suspended: 'tenantState.suspended.hint',
  tearing_down: 'tenantState.tearingDown.hint',
  removed: 'tenantState.removed.hint',
};

export function tenantStateLabelKey(state: string): SimpleKey {
  return isTenantState(state) ? (TENANT_STATE_LABEL[state] ?? 'tenantState.unknown') : 'tenantState.unknown';
}

export function tenantStateHintKey(state: string): SimpleKey {
  return isTenantState(state) ? (TENANT_STATE_HINT[state] ?? 'tenantState.unknown.hint') : 'tenantState.unknown.hint';
}

/**
 * What a tenancy's state is, with what it means on the badge.
 *
 * The meaning used to be a sentence beside it, on a card and in a band and in a
 * table cell. It is one sentence per state and it is true of every tenancy, so
 * it goes where a meaning goes: on the thing it is the meaning of.
 */
export function TenantStateBadge({ state }: { state: string }) {
  const { t } = useI18n();
  return (
    <StatusBadge
      tone={TENANT_STATE_TONE[state] ?? 'neutral'}
      label={t(tenantStateLabelKey(state))}
      hint={t(tenantStateHintKey(state))}
    />
  );
}

const IDENTITY_STATE_TONE: Record<string, Tone> = {
  pending: 'neutral',
  applying: 'info',
  applied: 'ok',
  rejected: 'warn',
  failed: 'deny',
};

const IDENTITY_STATE_LABEL: Record<string, SimpleKey> = {
  pending: 'identityState.pending.badge',
  applying: 'identityState.applying.badge',
  applied: 'identityState.applied.badge',
  rejected: 'identityState.rejected.badge',
  failed: 'identityState.failed.badge',
};

export function IdentityStateBadge({ state }: { state: string }) {
  const { t } = useI18n();
  return (
    <StatusBadge
      tone={IDENTITY_STATE_TONE[state] ?? 'neutral'}
      label={t(IDENTITY_STATE_LABEL[state] ?? 'identityState.unknown.badge')}
    />
  );
}

const OUTCOME_TONE: Record<string, Tone> = {
  accepted: 'ok',
  not_accepted: 'warn',
  failed: 'deny',
  unchanged: 'neutral',
  applied: 'ok',
  skipped: 'neutral',
};

const OUTCOME_LABEL: Record<string, SimpleKey> = {
  accepted: 'reconcile.outcome.accepted',
  not_accepted: 'reconcile.outcome.notAccepted',
  failed: 'reconcile.outcome.failed',
  unchanged: 'reconcile.step.unchanged',
  applied: 'reconcile.step.applied',
  skipped: 'reconcile.step.skipped',
};

/** The outcome of a whole reconcile, or of one of its steps. */
export function OutcomeBadge({ outcome }: { outcome: string }) {
  const { t } = useI18n();
  return (
    <StatusBadge
      tone={OUTCOME_TONE[outcome] ?? 'neutral'}
      label={t(OUTCOME_LABEL[outcome] ?? 'reconcile.outcome.unknown')}
    />
  );
}
